import { VehiclePayload, VehicleResponse } from "api/types";
import { getAvailableVehicleData } from "api/vehicles/vehicle";
import { formatDateTime, useData } from "data/data-provider";
import { CarListingModal } from "models/vehicleModal";
import React, { FC, useEffect, useState } from "react";
import FleetCard from "./FleetCard";
import { formatDateAndTimeToISO } from "utils/date-format";

interface VehicleSwapProp {
  bookingDetails: any;
  setSelected?: any;
}
const VehicleSwap: FC<VehicleSwapProp> = ({ bookingDetails, setSelected }) => {
  const [loadingAvailableFleet, setLoadingAvailableFleet] =
    useState<boolean>(false);
  const [availableFleet, setAvailableFleet] = useState<VehicleResponse[]>([]);
  const [filteredFleet, setFilteredFleet] = useState<CarListingModal[] | []>(
    availableFleet ?? [],
  );
  const {
    startDate,
    startTime,
    endTime,
    endDate,

    pickupLocation,
    dropOffLocation,

    setSelectedCar,
    selectedCar,
  } = useData();
  const [loading, setLoading] = useState(false);
  const [defaultBookingVehicle, setDefaultBookingVehicle] = useState<any>(null);

  useEffect(() => {
    if (bookingDetails?.fleet) {
      setDefaultBookingVehicle(bookingDetails.fleet);
      if (!selectedCar) {
        setSelectedCar(bookingDetails.fleet);
      }
    }
  }, [bookingDetails]);

  useEffect(() => {
    callAvailableFleetAPI();
  }, []);

  const callAvailableFleetAPI = async () => {
    setLoadingAvailableFleet(true);

    if (!startDate || !endDate) {
      console.error("Start date or end date is not set");
      setLoadingAvailableFleet(false);
      return;
    }

    const vehiclePayload: VehiclePayload = {
      available: true,
      start_time: startDate && startTime ? formatDateAndTimeToISO(startDate, startTime) : "",
      end_time: endDate && endTime ? formatDateAndTimeToISO(endDate, endTime) : "",
      pickup_location: pickupLocation?.id,
      dropoff_location: dropOffLocation?.id,
      booking_id: bookingDetails?.id,
    };
    const transformCarListingToVehicleResponse = (
      carListing: CarListingModal[],
    ): VehicleResponse[] => {
      return carListing.map((car) => ({
        ...car,
      }));
    };

    try {
      const result = await getAvailableVehicleData(vehiclePayload);

      setLoadingAvailableFleet(false);

      if (result.error) {
        setLoadingAvailableFleet(false);
      } else if (result.data) {
        const activeVehicles = (result.data as CarListingModal[]).filter(
          (vehicle) => vehicle.active,
        );
        setAvailableFleet(transformCarListingToVehicleResponse(activeVehicles));
      }
    } catch (error) {
      console.error("An unexpected error occurred:", error);
      setLoadingAvailableFleet(false);
    } finally {
      setLoadingAvailableFleet(false);
    }
  };

  const handleVehicleClick = (car: any) => {
    if (selectedCar && selectedCar.id === car.id) {
      setSelectedCar(defaultBookingVehicle);
    } else {
      setSelectedCar(car);
    }
  };

  const renderCard = (car: any) => {
    return (
      <FleetCard
        key={car.id}
        data={car}
        simpleImg={true}
        clickFunction={setSelected}
        onClick={() => handleVehicleClick(car)}
        loading={loading}
      />
    );
  };
  return (
    <div>
      <div className="flex w-full items-center gap-2 px-4 py-3 bg-yellow-50 border border-yellow-200  font-semibold rounded-lg transition-colors shadow-lg">
        <p className="text-yellow-800">
          <span className="font-semibold">Note:</span> If you don't select a
          different vehicle, your current vehicle will remain selected by
          default. If you select and deselect the same vehicle, it will revert
          to the default selection.
        </p>
      </div>
      {loadingAvailableFleet ? (
        <div className="flex justify-center my-12">
          <svg
            aria-hidden="true"
            className="w-6 h-6 text-gray-200 animate-spin dark:text-gray-600 fill-blue-400"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
        </div>
      ) : (
        <div className="nc-SectionGridFeaturePlaces relative  mt-12">
          <div className={`grid gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3 `}>
            {availableFleet?.map(
              (car: any) => car?.active === true && renderCard(car),
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleSwap;